import React, { useCallback, useEffect } from 'react';

import { Outlet } from 'react-router-dom';

import CommunityAPI from '../../api/CommunityAPI';
import {
  setActiveSection,
  setCommunityLinkList,
  setGroupChannelList,
  setMetadata,
} from '../../context/communityReducer';
import showAppError from '../../shared/error';
import { useAppDispatch, useAppSelector } from '../../shared/hooks';
import CommunityPostLikedByUsersModal from './CommunityPost/CommunityPostItems/CommunityPostLikedByUsersModal';
import CommunityChannelInfo from './components/CommunityChannelInfo';
import CommunityGlobalCarousel from './components/CommunityGlobalCarousel/CommunityGlobalCarousel';

type Props = {
  children?: React.ReactNode;
};

const CommunityWrapper: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();

  const { selectedChannelData } = useAppSelector((state) => state.community);
  const { chatRoomDetails } = useAppSelector(
    (state) => state?.communityMessage,
  );
  const searchParams = new URLSearchParams(window.location.search);
  const queryInfoValue = searchParams.get('info');

  const fetchMetaData = useCallback(
    async (channelIds: string[]) => {
      try {
        if (!channelIds || channelIds.length === 0) {
          return;
        }
        const resp = await CommunityAPI.getMetaData(channelIds);
        dispatch(setMetadata(resp?.data?.result?.metadata || []));
      } catch (error) {
        showAppError(error);
      }
    },
    [dispatch],
  );

  const fetchAllChannelData = useCallback(async () => {
    try {
      const resp = await CommunityAPI.getAllChannelGroups();
      if (resp.status === 200) {
        const channelGroups = resp?.data?.result;
        dispatch(setGroupChannelList(channelGroups));

        const channelIds = channelGroups?.reduce((acc: string[], item) => {
          const eachChannelIds = item?.channelArr?.map(
            (channel: any) => channel?._id,
          );
          return [...acc, ...eachChannelIds];
        }, []);
        fetchMetaData(channelIds);
      }
    } catch (error) {
      showAppError(error);
    }
  }, [dispatch, fetchMetaData]);

  const fetchAllLinkData = useCallback(async () => {
    try {
      const resp = await CommunityAPI.getAllCommunityLinks();
      if (resp.status === 200) {
        dispatch(setCommunityLinkList(resp?.data?.result));
      }
    } catch (error) {
      showAppError(error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAllChannelData();
    fetchAllLinkData();
  }, [fetchAllChannelData, fetchAllLinkData]);

  useEffect(() => {
    return () => {
      dispatch(
        setActiveSection({
          activeSection: 'feed',
          headerTitle: 'Feed',
        }),
      );
    };
  }, [dispatch]);

  return (
    <>
      <Outlet />
      {children}
      {queryInfoValue === 'true' &&
      (selectedChannelData?._id || chatRoomDetails?._id) ? (
        <CommunityChannelInfo isChatRoomInfo={Boolean(chatRoomDetails?._id)} />
      ) : null}

      <CommunityGlobalCarousel />
      <CommunityPostLikedByUsersModal />
    </>
  );
};

export default CommunityWrapper;
